import { Text } from '@yes.technology/react-toolkit'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18n-lite'
import type { ChartWrapperOptions } from 'react-google-charts'
import {
  buildGanttCockpitTemplateProps,
  type GoogleGanttDataTable
} from 'site/Renderer/components/GanttChart/adaptGoogleGanttEnvelope'
import {
  ganttDefaultCaptions,
  sortGanttFooterMilestones
} from 'site/Renderer/components/GanttChart/Gantt.utils'
import GanttChart from 'site/Renderer/components/GanttChart/GanttChart'

import { Divider } from '@yes.technology/react-toolkit'
import { Title } from '../Cockpit.styles'
import GanttCockpitCaptions from './GanttCockpitCaptions'
import GanttCockpitMilestoneList from './GanttCockpitMilestoneList'
import {
  GanttCockpitRoadmapChartColumn,
  GanttCockpitRoadmapRow,
  GanttCockpitRoadmapSidebar
} from './GanttCockpitSidebar.styles'

export type GanttCockpitWrapperProps = {
  data: GoogleGanttDataTable
  options?: ChartWrapperOptions['options']
  title: string
}

const GanttCockpitWrapper = ({
  data,
  options,
  title
}: GanttCockpitWrapperProps) => {
  const { t } = useTranslation()
  const ganttProps = useMemo(
    () => buildGanttCockpitTemplateProps(data, options),
    [data, options]
  )

  useEffect(() => {
    if (ganttProps !== null) return
    const looksLikeNonEmptyTable =
      Array.isArray(data) &&
      data.length >= 2 &&
      Array.isArray(data[0]) &&
      data[0].length > 0
    if (looksLikeNonEmptyTable) {
      console.warn(
        '[GanttCockpitWrapper] Gantt dataset could not be adapted to native chart props.'
      )
    }
  }, [data, ganttProps])

  const sortedRoadmapMilestones = useMemo(
    () => sortGanttFooterMilestones(ganttProps?.footer?.milestones),
    [ganttProps?.footer?.milestones]
  )

  const roadmapCaptions = ganttProps?.captions ?? ganttDefaultCaptions

  if (!ganttProps) {
    return (
      <>
        <Title>{title}</Title>
        <Text variant='content-04' data-testid='gantt-cockpit-unavailable'>
          {t('gantt.cockpit.unavailable')}
        </Text>
      </>
    )
  }

  return (
    <>
      <Title>{title}</Title>
      <GanttCockpitRoadmapRow>
        <GanttCockpitRoadmapSidebar data-testid='gantt-cockpit-roadmap-sidebar'>
          <GanttCockpitMilestoneList milestones={sortedRoadmapMilestones} />
          {sortedRoadmapMilestones.length > 0 ? (
            <Divider heightClass='small' color='gray' />
          ) : null}
          <GanttCockpitCaptions captions={roadmapCaptions} />
        </GanttCockpitRoadmapSidebar>
        <GanttCockpitRoadmapChartColumn>
          <GanttChart props={ganttProps} />
        </GanttCockpitRoadmapChartColumn>
      </GanttCockpitRoadmapRow>
    </>
  )
}

export default GanttCockpitWrapper
